import React, { useState } from 'react';
import { Plus, ChevronDown, Search } from 'lucide-react';
import { Sensor } from '../../types';
import { formatSensorSerial } from '../../utils/sensorUtils';
import { useGetZoneSensorsQuery } from '../../services/zoneApi';

interface SensorAssignmentProps {
  zoneId: number;
  sensors: Sensor[];
  onAssign: (zoneId: number, sensorId: number) => void | Promise<void>;
}

export default function SensorAssignment({ zoneId, sensors, onAssign }: SensorAssignmentProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const { data: zoneSensors, isLoading } = useGetZoneSensorsQuery(zoneId);

  const assignedIds = React.useMemo(
    () => new Set((zoneSensors ?? []).map(s => Number(s.sensor_id))),
    [zoneSensors]
  );

  // Clear pending IDs that have now been confirmed by the server
  React.useEffect(() => {
    setPendingIds(prev => {
      const next = new Set(prev);
      prev.forEach(id => { if (assignedIds.has(id)) next.delete(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [assignedIds]);

  const unassignedSensors = React.useMemo(() => {
    if (!sensors) return [];
    return sensors.filter(s => !assignedIds.has(Number(s.id)) && !pendingIds.has(s.id));
  }, [sensors, assignedIds, pendingIds]);

  const visibleSensors = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return unassignedSensors;
    return unassignedSensors.filter(s =>
      s.name.toLowerCase().includes(q) ||
      formatSensorSerial(s.serialNo, s.type).toLowerCase().includes(q)
    );
  }, [unassignedSensors, search]);

  const allVisibleSelected = visibleSensors.length > 0 && visibleSensors.every(s => selectedIds.has(s.id));

  const toggleSelected = (sensorId: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(sensorId)) next.delete(sensorId); else next.add(sensorId);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleSensors.forEach(s => next.delete(s.id));
      else visibleSensors.forEach(s => next.add(s.id));
      return next;
    });
  };

  const handleAddSelected = async () => {
    const ids = unassignedSensors.filter(s => selectedIds.has(s.id)).map(s => s.id);
    if (ids.length === 0) return;
    setIsAdding(true);
    setPendingIds(prev => new Set([...prev, ...ids]));
    try {
      for (const sensorId of ids) {
        await Promise.resolve(onAssign(zoneId, sensorId));
      }
    } finally {
      setIsAdding(false);
      setSelectedIds(new Set());
      setSearch('');
      setIsOpen(false);
    }
  };

  if (isLoading) return null;
  if (unassignedSensors.length === 0) return null;

  return (
    <div className="relative mb-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2 text-sm bg-app-inset dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
      >
        <span className="flex items-center">
          <Plus className="h-4 w-4 mr-2" />
          Add Sensors
        </span>
        <ChevronDown className="h-4 w-4 ml-2" />
      </button>

      {isOpen && (
        <div className="relative z-10 mt-1 w-full bg-app-inset dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
          <div className="px-3 pt-2 pb-1 border-b border-gray-100 dark:border-gray-600">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search sensors…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            {visibleSensors.length > 1 && (
              <label className="flex items-center gap-2 px-1 pt-2 pb-1 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                />
                Select all{search ? ' matching' : ''} ({visibleSensors.length})
              </label>
            )}
          </div>
          <ul className="py-1 max-h-60 overflow-auto">
            {visibleSensors.length === 0 ? (
              <li className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">No sensors match your search</li>
            ) : (
              visibleSensors.map((sensor) => (
                <li key={sensor.id}>
                  <label className="w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded flex-shrink-0"
                      checked={selectedIds.has(sensor.id)}
                      onChange={() => toggleSelected(sensor.id)}
                    />
                    <div>
                      <div className="font-medium">{sensor.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatSensorSerial(sensor.serialNo, sensor.type)}
                      </div>
                    </div>
                  </label>
                </li>
              ))
            )}
          </ul>
          <div className="flex justify-end gap-2 px-3 py-2 border-t border-gray-100 dark:border-gray-600">
            <button
              onClick={() => { setIsOpen(false); setSelectedIds(new Set()); setSearch(''); }}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleAddSelected}
              disabled={selectedIds.size === 0 || isAdding}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md text-sm font-medium disabled:opacity-50"
            >
              {isAdding
                ? 'Adding…'
                : selectedIds.size === 0
                  ? 'Add sensors'
                  : `Add ${selectedIds.size} sensor${selectedIds.size === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
